import { api, type Page } from "@/lib/api/client";
import type {
  LeaveAccrualInput,
  LeaveAccrualResult,
  LeaveOpeningInput,
  LeaveOpeningResult,
  LeaveSummaryFilters,
  LeaveSummaryRow,
} from "./balance.types";
import type {
  ApplyLeaveRequestInput,
  ApplyOnBehalfLeaveRequestInput,
  CreateLeaveRequestInput,
  LeaveBalance,
  LeaveLedgerEntry,
  LeavePreview,
  LeaveRequest,
  LeaveRequestFilters,
} from "./leave.types";
import type {
  AbsenceCalendar,
  AbsenceCalendarFilters,
  LeavePolicyAssignInput,
  LeavePolicyAssignmentRow,
  LeavePolicyCreateInput,
  LeavePolicyFilters,
  LeavePolicyRow,
  LeavePolicyUpdateInput,
  LeaveTypeCreateInput,
  LeaveTypeFilters,
  LeaveTypeRow,
  LeaveTypeUpdateInput,
} from "./leave-config.types";

/**
 * Leave endpoints.
 *
 * One function per endpoint, no cleverness: this file is a transcription of the route table so
 * that finding "where is the cancel call" is a search rather than a reading exercise. Every
 * query string is built by the client from an object — a caller concatenating `?page=` is
 * exactly how a filter value with a `&` in it becomes a silent, unreproducible bug.
 */

const BASE = "/hr/leave-requests";
const TYPES = "/hr/leave-types";
const POLICIES = "/hr/leave-policies";

export const leaveApi = {
  list(filters: LeaveRequestFilters): Promise<Page<LeaveRequest>> {
    return api.page<LeaveRequest>(BASE, { params: { ...filters } });
  },

  get(id: string): Promise<LeaveRequest> {
    return api.get<LeaveRequest>(`${BASE}/${id}`);
  },

  /**
   * Every request in a window, for an aggregate.
   *
   * A dashboard chart needs the WHOLE year, and a single `limit=200` would be clamped to 100
   * without saying so. This walks the pages and reports whether it had to stop, so the chart
   * can state that it is showing a partial picture instead of pretending otherwise.
   *
   * A summary endpoint on the server would replace this outright, and should.
   */
  allInWindow(params: {
    legalEntityId?: string | undefined;
    employmentId?: string | undefined;
    startsFrom: string;
    startsTo: string;
  }) {
    return api.pageAll<LeaveRequest>(BASE, {
      params: { ...params, sortBy: "startDate", sortOrder: "asc" },
      maxPages: 10,
    });
  },

  /**
   * The server's own day/minute calculation for a draft.
   *
   * Called as the form changes, and it is the ONLY source of the day count the user sees.
   * Weekends, public holidays, the employee's shift calendar and half-day rules all live in
   * the leave engine; a browser-side estimate would disagree with it for exactly the people
   * whose leave is unusual.
   */
  preview(input: {
    /** Required by the server: the calculation is done against THIS person's calendar. */
    employmentId: string;
    leaveTypeId?: string;
    startDate: string;
    endDate: string;
    startDayPart?: string;
    endDayPart?: string;
  }): Promise<LeavePreview> {
    return api.post<LeavePreview>(`${BASE}/preview`, input);
  },

  /**
   * izin-sade 20 Ekim 2026 — «Onaya gönder»: OLUŞTUR + GÖNDER TEK İSTEK. Gönderme reddedilirse
   * (onaylayan yok, bakiye yetmiyor, çakışma) talep hiç oluşmaz; ekranda taslak kalmaz. Retry-unsafe
   * (ikinci basış ikinci talep), o yüzden form başına bir `Idempotency-Key`; ağ zaman aşımından sonra
   * aynı anahtarla tekrar `{ id }` döner (özet cevap), ikinci talep doğmaz.
   */
  apply(input: ApplyLeaveRequestInput, idempotencyKey: string): Promise<LeaveRequest> {
    return api.post<LeaveRequest>(`${BASE}/apply`, input, { idempotencyKey });
  },

  /** The same one-step filing for İK entering leave for somebody else (separate right, no attachment). */
  applyOnBehalf(input: ApplyOnBehalfLeaveRequestInput): Promise<LeaveRequest> {
    return api.post<LeaveRequest>(`${BASE}/apply-on-behalf`, input);
  },

  /**
   * Eski taslak ucu. Ekranlar artık taslak üretmiyor (`apply`); uç, veritabanlarında kalmış taslaklar
   * ve API çağıranlar için duruyor.
   */
  create(input: CreateLeaveRequestInput): Promise<LeaveRequest> {
    return api.post<LeaveRequest>(BASE, input);
  },

  /** HR entering leave for somebody else. A separate endpoint because it is a separate right. */
  createOnBehalf(input: CreateLeaveRequestInput): Promise<LeaveRequest> {
    return api.post<LeaveRequest>(`${BASE}/on-behalf`, input);
  },

  /**
   * Talebe SONRADAN rapor/belge ekler («Rapor ekle»). `fileId`: çağıranın yüklediği, taranmış
   * `personnel_document` dosyası (`uploadPersonnelDocumentFile`). Cevap talebin güncel görünümü.
   * Retry-unsafe (ikinci basış ikinci belge) → `Idempotency-Key`.
   */
  attachDocument(id: string, fileId: string, idempotencyKey: string): Promise<LeaveRequest> {
    return api.post<LeaveRequest>(`${BASE}/${id}/documents`, { fileId }, { idempotencyKey });
  },

  update(id: string, input: Partial<CreateLeaveRequestInput>): Promise<LeaveRequest> {
    return api.patch<LeaveRequest>(`${BASE}/${id}`, input);
  },

  /**
   * Draft → submitted. The point at which the approval chain is built.
   *
   * EVERY TRANSITION CARRIES THE VERSION. The server compares it with the stored row and
   * refuses a mismatch, so two people acting on one request cannot overwrite each other — the
   * second gets a conflict instead of a silent win. The caller must therefore pass the version
   * from a record it has actually read.
   */
  submit(id: string, version: number, note?: string): Promise<LeaveRequest> {
    return api.post<LeaveRequest>(`${BASE}/${id}/submit`, { version, ...(note ? { note } : {}) });
  },

  /**
   * Taking back one's OWN request before a decision.
   *
   * GEREKÇE İSTEĞE BAĞLI (30 Ekim 2026): ekran sormuyor; sunucu verilmezse onay geçmişine
   * «Talep sahibi geri çekti.» yazıyor. Verilirse en az on karakter olmalı — boş dize gönderilmez.
   */
  withdraw(id: string, version: number, reason?: string): Promise<LeaveRequest> {
    return api.post<LeaveRequest>(`${BASE}/${id}/withdraw`, { version, ...(reason ? { reason } : {}) });
  },

  /**
   * Cancelling an APPROVED leave. Not the same act as withdrawing: the days have already been
   * deducted, so this asks for a reversal and may itself need approval.
   */
  cancel(id: string, version: number, reason: string): Promise<LeaveRequest> {
    return api.post<LeaveRequest>(`${BASE}/${id}/cancel`, { version, reason });
  },

  approve(id: string, version: number, note?: string): Promise<LeaveRequest> {
    return api.post<LeaveRequest>(`${BASE}/${id}/approve`, { version, ...(note ? { note } : {}) });
  },

  reject(id: string, version: number, reason: string): Promise<LeaveRequest> {
    return api.post<LeaveRequest>(`${BASE}/${id}/reject`, { version, reason });
  },

  remove(id: string): Promise<void> {
    return api.delete(`${BASE}/${id}`);
  },

  /**
   * The absence calendar — ONE OBJECT, not a list of requests.
   *
   * This signature said `LeaveRequest[]`. The endpoint has always answered
   * `{ from, to, entries, holidays, truncated }`, so the first screen to call it would have
   * mapped over `undefined`. It had no caller yet, which is exactly how a wrong type survives.
   */
  calendar(params: AbsenceCalendarFilters): Promise<AbsenceCalendar> {
    return api.get<AbsenceCalendar>(`${BASE}/calendar`, { params: { ...params } });
  },

  /**
   * The leave-type catalogue.
   *
   * NO `legalEntityId`. The server's query schema does not declare one — leave types belong to
   * the customer, not to a company inside it — so sending it was a parameter that travelled on
   * every call and narrowed nothing. It did not fail, which is why it survived: `zListQuery`
   * composes plain objects, so an unknown key is STRIPPED rather than refused, and the call
   * looked like it was scoping a list it was not scoping.
   */
  types(params: LeaveTypeFilters = {}): Promise<Page<LeaveTypeRow>> {
    return api.page<LeaveTypeRow>(TYPES, { params: { ...params } });
  },

  createType(input: LeaveTypeCreateInput): Promise<LeaveTypeRow> {
    return api.post<LeaveTypeRow>(TYPES, input);
  },

  /**
   * `version` is part of the body, not a header: the server compares it with the stored row
   * and answers 409 on a mismatch, so two people editing one type cannot overwrite each other.
   * The caller must therefore pass the version it actually read.
   */
  updateType(id: string, input: LeaveTypeUpdateInput): Promise<LeaveTypeRow> {
    return api.patch<LeaveTypeRow>(`${TYPES}/${id}`, input);
  },

  /**
   * A soft delete, and the screen must say so. The row leaves the pickers; the requests,
   * balances and ledger entries already filed under it stay exactly as they are — an
   * entitlement history that lost its type would be unreadable and unauditable.
   */
  removeType(id: string, version?: number): Promise<void> {
    return api.delete(`${TYPES}/${id}`, {
      ...(version === undefined ? {} : { params: { version } }),
    });
  },

  restoreType(id: string): Promise<LeaveTypeRow> {
    return api.post<LeaveTypeRow>(`${TYPES}/${id}/restore`);
  },

  /**
   * A person's balances for one cycle.
   *
   * `employmentId` is REQUIRED and the other two names are the server's: `leaveTypeId` and
   * `at`. The query schema is `z.strictObject`, so an unknown key is a 400 rather than an
   * ignored parameter — this signature used to send `legalEntityId` and `asOf`, neither of
   * which exists, and both of which would have been refused the first time anybody called it.
   */
  balances(params: { employmentId: string; leaveTypeId?: string; at?: string }): Promise<
    LeaveBalance[]
  > {
    return api.get<LeaveBalance[]>("/hr/leave-balances", { params });
  },

  /**
   * The entitlement ledger — PAGED, not cursored.
   *
   * The handler answers with `paginated(...)`, so the envelope carries `meta.pagination` and
   * no `meta.cursor`. Read through `api.cursor` it returned `hasMore: false` on every call:
   * a ledger that silently showed its first page and claimed there was nothing after it. The
   * two ends had never met because nothing called this yet.
   */
  ledger(params: {
    employmentId: string;
    leaveTypeId?: string;
    page?: number;
    limit?: number;
  }): Promise<Page<LeaveLedgerEntry>> {
    return api.page<LeaveLedgerEntry>("/hr/leave-balances/ledger", { params });
  },

  /**
   * ik-izin 6 Ekim 2026: «İzin Tablosu» — every active employment of ONE company, paged, with
   * its seniority, this year's band and the cumulative figures in working days.
   */
  summary(legalEntityId: string, params: LeaveSummaryFilters): Promise<Page<LeaveSummaryRow>> {
    return api.page<LeaveSummaryRow>("/hr/leave-balances/summary", {
      params: { ...params, legalEntityId },
    });
  },

  /**
   * «Hak edişleri güncelle»: writes every completed cycle's ACCRUAL that is missing, for one
   * employment or a whole company. Idempotent — a second run answers zeros.
   */
  accrue(input: LeaveAccrualInput): Promise<LeaveAccrualResult> {
    return api.post<LeaveAccrualResult>("/hr/leave-balances/accrue", input);
  },

  /**
   * «Açılış kaydı»: usage before the system went live, drawn FIFO from the oldest years. The
   * server converts the days with the person's own working day; CRITICAL, so it may ask for a
   * fresh password (`useReauth`).
   */
  opening(input: LeaveOpeningInput): Promise<LeaveOpeningResult> {
    return api.post<LeaveOpeningResult>("/hr/leave-balances/opening", input);
  },

  /* ------------------------------------------------------------- policies */

  policies(params: LeavePolicyFilters): Promise<Page<LeavePolicyRow>> {
    return api.page<LeavePolicyRow>(POLICIES, { params: { ...params } });
  },

  policy(id: string): Promise<LeavePolicyRow> {
    return api.get<LeavePolicyRow>(`${POLICIES}/${id}`);
  },

  createPolicy(input: LeavePolicyCreateInput): Promise<LeavePolicyRow> {
    return api.post<LeavePolicyRow>(POLICIES, input);
  },

  updatePolicy(id: string, input: LeavePolicyUpdateInput): Promise<LeavePolicyRow> {
    return api.patch<LeavePolicyRow>(`${POLICIES}/${id}`, input);
  },

  removePolicy(id: string, version?: number): Promise<void> {
    return api.delete(`${POLICIES}/${id}`, {
      ...(version === undefined ? {} : { params: { version } }),
    });
  },

  /** Attaches a policy to one employment for a window. Its own route, its own reason. */
  assignPolicy(id: string, input: LeavePolicyAssignInput): Promise<LeavePolicyAssignmentRow> {
    return api.post<LeavePolicyAssignmentRow>(`${POLICIES}/${id}/assignments`, input);
  },
};
